import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { useSwipeable } from 'react-swipeable';
import { Button } from '../../../../flayr-components/Button/Button';
import { Flex } from '../../../../flayr-components/Flex/Flex';
import { Heading } from '../../../../flayr-components/Heading/Heading';
import { Input } from '../../../../flayr-components/Input/Input';
import ArrayMap from '../../../../lib/ArrayMap/ArrayMap';
import Logo from '../../../../lib/Logo/Logo';
import Overlay from '../../../../lib/Overlay/Overlay';
import PicWall from '../../../../lib/PicWall/PicWall';
import RadioBtn from '../../../../lib/RadioBtn/RadioBtn';
import { IInputField } from '../../useReviewFormPageData';

interface IReviewFormItemProps {
  field: IInputField;
  providerName?: string;
  customerName?: string;
  index?: number;
  className?: string;
  value?: string | number | string[];
  isLastStep?: boolean;
  errors?: any;
  isScreenActive?: boolean;
  onNext?: (e?: any) => void;
  onPrev?: (e?: any) => void;
  onInputChange?: (e?: any) => void;
  onSave?: (e?: any, skipNext?: boolean) => void;
  onSubmit?: (e?: any) => void;
  onMediaChange?: (e?: any) => void;
}

const ReviewFormItem: React.FC<React.PropsWithChildren<IReviewFormItemProps>> = ({
  field,
  providerName,
  customerName,
  index = 0,
  isScreenActive,
  className = '',
  value,
  isLastStep,
  errors = {},
  onNext,
  onPrev,
  onSave,
  onSubmit,
  onMediaChange,
  onInputChange,
}) => {
  const handlers = useSwipeable({
    onSwipedUp: onNext,
    onSwipedDown: onPrev,
  });
  function replaceName(text) {
    if (!text) {
      return '';
    }

    return (text || '')
      .replace(/{{providerName}}/g, providerName)
      .replace(/{{customerName}}/g, customerName);
  }

  function handleInputChange(e) {
    const { value, dataset } = e.target;
    switch (field.type) {
      case 'textInput':
        onInputChange?.({
          name: field.name,
          value,
          isTrafficControl: field.isTrafficControl,
        });
        break;
      case 'yesOrNo':
        onSave?.({
          name: field.name,
          value: +dataset.value,
          isTrafficControl: field.isTrafficControl,
        });
        break;
      default:
        onSave?.({
          name: field.name,
          value: +value,
          isTrafficControl: field.isTrafficControl,
        });
    }
  }

  function handleSave() {
    if (isLastStep) {
      onSubmit?.();
    } else {
      onSave?.({
        name: field.name,
        value,
        isTrafficControl: field.isTrafficControl,
      });
    }
  }

  function handleImgUpload(url) {
    onMediaChange?.({
      name: field.name,
      value: [url, ...((Array.isArray(value) && value) || [])],
    });
  }

  function handleImgDelete(e) {
    const { id } = e.target;
    const newMedia = (value as string[])?.filter((_, idx) => idx !== +id);
    onMediaChange?.({
      name: field.name,
      value: newMedia,
    });
  }

  function handleBlur(e) {
    const { name, value } = e.target;
    onSave?.(
      {
        name,
        value,
      },
      true
    );
  }

  return (
    <Overlay
      className={`flex flex-col items-center justify-center max-h-screen overflow-y-auto bg-gray-100 ${className} py-5`}
    >
      <div className="w-full -mt-8" {...handlers}>
        <div className="mb-8 text-center">
          <Logo className="text-2xl" />
        </div>
        <div className="flex flex-none w-full text-gray-700 lg:mx-auto lg:w-96">
          <div className="flex-none w-5 pt-1 -mt-px">{index + 1}.</div>
          <div className="flex-grow">
            <Heading size="xl">
              {replaceName(
                (typeof field.label === 'object' &&
                  (field.label as any).default) ||
                  field.label
              )}{' '}
              {field.isRequired && '*'}
            </Heading>

            <div className="w-full">
              <div className="text-lg leading-tight text-gray-400">
                {replaceName(
                  (typeof field.description === 'object' &&
                    (field.description as any).default) ||
                    field.description
                )}
              </div>
              <div className="w-full mt-5">
                {field.type === 'textInput' && (
                  <div className="w-full bg-white border rounded">
                    <Input
                      type="textarea"
                      autoExpandHeight
                      rows={6}
                      name={field.name}
                      value={value as string}
                      inputClassName="border-opacity-0 focus:border-opacity-0 bg-white bg-opacity-0"
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      mb="0"
                      size="lg"
                    />
                  </div>
                )}

                {field.type === '1to5' && (
                  <>
                    <div className="flex overflow-hidden border divide-x rounded-lg">
                      <ArrayMap
                        items={[1, 2, 3, 4, 5]}
                        render={(number) => (
                          <RadioBtn
                            name={field.name}
                            value={number}
                            checked={value === number}
                            onChange={handleInputChange}
                            className="flex justify-center flex-grow h-16 text-xl items-ceter"
                            unCheckedClassName=""
                            checkedClassName="text-white bg-gray-700"
                            btnClassName={classNames({
                              'rounded-r-none': number === 1,
                              'rounded-l-none': number === 5,
                              'rounded-0': number > 1 && number < 5,
                            })}
                          >
                            {number}
                          </RadioBtn>
                        )}
                      />
                    </div>
                    <Flex x="between" className="mt-2 text-sm text-gray-500">
                      <span>Bad</span>
                      <span>Good</span>
                    </Flex>
                  </>
                )}

                {field.type === 'yesOrNo' && (
                  <Flex column y="start">
                    <YesNoBtn
                      text="yes"
                      value="5"
                      onClick={handleInputChange}
                      active={value === 5}
                      isScreenActive={isScreenActive}
                    />
                    <YesNoBtn
                      text="no"
                      value="1"
                      onClick={handleInputChange}
                      active={value === 1}
                      isScreenActive={isScreenActive}
                    />
                  </Flex>
                )}

                {field.type === 'media' && (
                  <PicWall
                    className="py-3"
                    images={(Array.isArray(value) && value) || []}
                    onUpload={handleImgUpload}
                    onDelete={handleImgDelete}
                    profileId="review"
                  />
                )}
              </div>
              {errors?.[field.name] && !value && (
                <Flex className="px-4 py-1 mt-4 text-sm text-red-400 bg-red-100 rounded">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="mr-3"
                  />
                  Please fill this in.
                </Flex>
              )}
              <div className="mt-3">
                <Button
                  className={classNames(
                    'transition-all transform duration-200',
                    {
                      'opacity-0 translate-y-5': !value,
                      'opacity-100 translate-none': value,
                    }
                  )}
                  size="lg"
                  color="dark"
                  block={isLastStep}
                  onClick={handleSave}
                >
                  {(isLastStep && 'Submit') || 'OK'}{' '}
                  <FontAwesomeIcon icon={faCheck} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

const YesNoBtn = ({
  text,
  onClick,
  value = '',
  active = false,
  isScreenActive = false,
}) => {
  React.useEffect(() => {
    if (isScreenActive) {
      const handleKeyDown = (e: KeyboardEvent) => {
        const keyToWatch = text[0].toLowerCase();
        const keyPressed = e.key.toLowerCase();
        if (keyPressed === keyToWatch) {
          onClick?.({ target: { dataset: { value } } });
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isScreenActive]);

  return (
    <button
      className={classNames(
        'flex items-center w-40 col-span-5 px-3 py-2 my-2 capitalize bg-gray-400 bg-opacity-25  rounded focus:outline-none',
        {
          'border-gray-600 border-2': active,
          'border-gray-500 border': !active,
        }
      )}
      onClick={onClick}
      data-value={value}
    >
      <span
        className={classNames(
          'inline-flex items-center justify-center w-6 h-6 mr-2 text-xs  border border-gray-400 rounded pointer-events-none',
          {
            'bg-gray-200': !active,
            'bg-gray-700 text-gray-200': active,
          }
        )}
      >
        {text[0]}
      </span>
      {text}

      {active && <FontAwesomeIcon icon={faCheck} className="ml-4 text-lg" />}
    </button>
  );
};

export default ReviewFormItem;
